import { createContext, Dispatch, FC, PropsWithChildren, SetStateAction, useCallback, useMemo, useState } from 'react';

export type UnifiedProfilesValuesApi = {
  searchText: string;
  selectedProfiles: Set<string>;
};

type UnifiedProfilesActionsApi = {
  setSearchText: Dispatch<SetStateAction<string>>;
  toggleSingleProfile: (email: string) => void;
  toggleManyProfiles: (emails: string[]) => void;
};

export const UnifiedProfilesValuesContext = createContext<UnifiedProfilesValuesApi | null>(null);
export const UnifiedProfilesActionsContext = createContext<UnifiedProfilesActionsApi | null>(null);

export const UnifiedProfilesProvider: FC<PropsWithChildren> = ({ children }) => {
  const [searchText, setSearchText] = useState('');
  const [selectedProfiles, setSelectedProfiles] = useState<Set<string>>(new Set());

  const toggleSingleProfile = useCallback(
    (id: string) => {
      setSelectedProfiles((prev) => {
        const isSelected = prev.has(id);

        if (isSelected) {
          prev.delete(id);
          return new Set(prev);
        }

        return new Set([...prev, id]);
      });
    },
    [setSelectedProfiles]
  );

  const toggleManyProfiles = useCallback(
    (emails: string[]) => {
      setSelectedProfiles((prev) => {
        const isAllSelected = emails.every((e) => prev.has(e));

        if (isAllSelected) {
          return new Set([]);
        }
        return new Set(emails);
      });
    },
    [setSelectedProfiles]
  );

  const value = useMemo(
    () =>
      ({
        searchText,
        selectedProfiles,
      }) satisfies UnifiedProfilesValuesApi,
    [selectedProfiles, searchText]
  );

  const actions = useMemo(
    () =>
      ({
        setSearchText,
        toggleSingleProfile,
        toggleManyProfiles,
      }) satisfies UnifiedProfilesActionsApi,
    [toggleSingleProfile, setSearchText]
  );

  return (
    <UnifiedProfilesValuesContext.Provider value={value}>
      <UnifiedProfilesActionsContext.Provider value={actions}>{children}</UnifiedProfilesActionsContext.Provider>
    </UnifiedProfilesValuesContext.Provider>
  );
};
